import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import chunk from 'lodash.chunk';
import isEmpty from 'lodash.isempty';
import Button from '../button/Button';
import { ELLIPSES } from '../../constants/shared/CommonConstants';
import './Pagination.scss';
import { generateReactKey } from '../../utils/shared/commonUtils';

const SLOTS_COUNT = 7;
const ELLIPSES_STEP = 3;
const FIRST_PAGE_INDEX = 0;
const SECOND_PAGE_INDEX = 1;
const THIRD_PAGE_INDEX = 2;
const FOURTH_PAGE_INDEX = 3;
const FIFTH_PAGE_INDEX = 4;
const SIXTH_PAGE_INDEX = 5;
const SEVENTH_PAGE_INDEX = 6;

const getLastItem = itemsList => itemsList.length;

const getSecondLastItem = itemsList => itemsList.length - 1;

const isEllipsesActivated = (item1, item2) => item1 - item2 > ELLIPSES_STEP;

const calculateMiddleSlot = (itemsList, pages, selectedPage) => {
    const modifiedPages = pages;
    const offset = isEllipsesActivated(itemsList.length, selectedPage)
        ? 0
        : itemsList.length - selectedPage - ELLIPSES_STEP;

    modifiedPages[THIRD_PAGE_INDEX] = selectedPage - 1 + offset;
    modifiedPages[FOURTH_PAGE_INDEX] = selectedPage + offset;
    modifiedPages[FIFTH_PAGE_INDEX] = selectedPage + 1 + offset;
    return modifiedPages;
};

const buildPagesList = (itemsList, selectedPage) => {
    const pagesList = itemsList.map((item, index) => index + 1);

    if (pagesList.length <= SLOTS_COUNT) return pagesList;

    const pages = [...Array(SLOTS_COUNT).keys()].map(item => item + 1);

    if (isEllipsesActivated(selectedPage, pages[FIRST_PAGE_INDEX])) {
        pages[SECOND_PAGE_INDEX] = ELLIPSES;
        calculateMiddleSlot(itemsList, pages, selectedPage);
    }
    pages[SIXTH_PAGE_INDEX] = isEllipsesActivated(itemsList.length, selectedPage)
        ? ELLIPSES
        : getSecondLastItem(itemsList);

    pages[SEVENTH_PAGE_INDEX] = getLastItem(itemsList);
    return pages;
};

const Pagination = ({
    buttonsClassName,
    generalSectionClassName,
    numberWrapperClassName,
    pageItemsCount,
    defaultNumberClassName,
    selectedNumberClassName,
    defaultEllipsesClassName,
    onSelect,
    onLrsSelect,
    items,
    lrsGroup,
}) => {
    const showPagination = items.length > pageItemsCount;
    const initialItemsList = chunk(items, pageItemsCount);

    const [itemsList, setItemsList] = useState(initialItemsList);
    const [pagesList, setPageList] = useState(buildPagesList(itemsList, 1));
    const [selectedPage, setSelectedPage] = useState(1);

    const getPartOfActivities = page => {
        if (page !== ELLIPSES) {
            setSelectedPage(page);
            onSelect(itemsList[page - 1]);
        }
    };

    useEffect(() => {
        getPartOfActivities(selectedPage);
    }, []);

    useEffect(() => {
        getPartOfActivities(selectedPage);
    }, [itemsList, pagesList, selectedPage]);

    useEffect(() => {
        setItemsList(initialItemsList);
        setPageList(buildPagesList(initialItemsList, 1));
        setSelectedPage(1);
    }, [items]);

    useEffect(() => {
        getPartOfActivities(selectedPage);
        setPageList(buildPagesList(initialItemsList, selectedPage));

        if (!isEmpty(itemsList)) {
            const activityIds = itemsList[selectedPage - 1]?.map(({ id }) => id);
            onLrsSelect(selectedPage, activityIds, pagesList, lrsGroup);
        }
    }, [selectedPage]);

    const updateSelectedPage = update => {
        setSelectedPage(selectedPage + update);
    };

    const getClassNameForItem = item => {
        let className = defaultNumberClassName;
        if (item === selectedPage) {
            className = `${defaultNumberClassName} ${selectedNumberClassName}`;
        } else if (item === ELLIPSES) {
            className = defaultEllipsesClassName;
        }
        return className;
    };

    const handlePageNumberClick = item => () => {
        getPartOfActivities(item);
    };

    const renderPagesList = () =>
        !isEmpty(pagesList) &&
        pagesList.map((item, index) => (
            <Button
                key={generateReactKey(item, item === ELLIPSES && index)}
                className={getClassNameForItem(item)}
                onClick={handlePageNumberClick(item)}
                text={item}
            />
        ));

    return (
        showPagination && (
            <div className="pagination-wrapper">
                <div className={generalSectionClassName}>
                    {selectedPage !== 1 && (
                        <Button
                            onClick={() => updateSelectedPage(-1)}
                            type="button"
                            className={`button-back ${buttonsClassName}`}
                            text={<i className="icon-amplify-arrow-left icon-back icons-general" />}
                        />
                    )}
                    <div className={numberWrapperClassName}>{renderPagesList()}</div>
                    {selectedPage !== itemsList.length && (
                        <Button
                            onClick={() => updateSelectedPage(1)}
                            type="button"
                            className={`button-next ${buttonsClassName}`}
                            text={
                                <i className="icon-amplify-arrow-right icon-next icons-general" />
                            }
                        />
                    )}
                </div>
            </div>
        )
    );
};

Pagination.defaultProps = {
    items: [],
    onSelect: () => {},
    onLrsSelect: () => {},
    pageItemsCount: 9,
    defaultNumberClassName: 'default-state',
    defaultEllipsesClassName: 'default-ellipses',
    selectedNumberClassName: 'selected-state',
    buttonsClassName: 'buttons-general',
    generalSectionClassName: 'pagination-section',
    numberWrapperClassName: 'list-container',
    lrsGroup: '',
};

Pagination.propTypes = {
    items: PropTypes.array,
    onSelect: PropTypes.func,
    onLrsSelect: PropTypes.func,
    pageItemsCount: PropTypes.number,
    defaultNumberClassName: PropTypes.string,
    defaultEllipsesClassName: PropTypes.string,
    selectedNumberClassName: PropTypes.string,
    buttonsClassName: PropTypes.string,
    generalSectionClassName: PropTypes.string,
    numberWrapperClassName: PropTypes.string,
    lrsGroup: PropTypes.string,
};

export default Pagination;
